import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { createLogger } from '../lib/logger';
import { appSurface, CAMPAIGNS_AUTHORITY_CODE } from '../lib/surface';

const logger = createLogger('Auth');

export interface GoProfile {
  id: string;
  organisation_id: string;
  auth_user_id: string | null;
  email: string;
  name: string;
  role: string;
  is_active: boolean;
}

export interface GoOrganisation {
  id: string;
  name: string;
  contact_email: string;
  subscription_tier: string | null;
  /**
   * Which product this company may open — 'go' or 'main'. Read straight off the
   * row Go already loads, rather than through organisation_surface(): it is the
   * same column, and there is no reason to make a second round trip for it.
   */
  surface: string | null;
  /** Whether the Campaigns module is switched on. Meaningful for surface='main' only. */
  campaigns_enabled: boolean | null;
  public_slug: string | null;
  public_portal_token: string;
}

interface AuthContextType {
  user: SupabaseUser | null;
  session: Session | null;
  profile: GoProfile | null;
  organisation: GoOrganisation | null;
  loading: boolean;
  /**
   * Whether this person holds the CAMPAIGNS authority in the Manual of
   * Authorities. Fetched only on the campaigns host — Go never asks, because
   * Go organisations do not hold authorities. Undefined while resolving, and
   * unresolved is not permission.
   */
  hasCampaignsAuthority: boolean | undefined;
  /** True when the signed-in user owns the business (role 'sme_admin') */
  isOwner: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshOrganisation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SupabaseUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<GoProfile | null>(null);
  const [organisation, setOrganisation] = useState<GoOrganisation | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasCampaignsAuthority, setHasCampaignsAuthority] = useState<boolean | undefined>(
    undefined,
  );

  const fetchOrganisation = useCallback(async (organisationId: string): Promise<GoOrganisation | null> => {
    const { data, error } = await supabase
      .from('organisations')
      .select('id, name, contact_email, subscription_tier, surface, campaigns_enabled, public_slug, public_portal_token')
      .eq('id', organisationId)
      .maybeSingle();
    if (error) {
      logger.warn('Failed to fetch organisation', error);
      return null;
    }
    return data as GoOrganisation | null;
  }, []);

  const fetchProfileRow = useCallback(async (authUserId: string): Promise<GoProfile | null> => {
    const { data, error } = await supabase
      .from('users')
      .select('id, organisation_id, auth_user_id, email, name, role, is_active')
      .eq('auth_user_id', authUserId)
      .maybeSingle();
    if (error) {
      logger.warn('Failed to fetch profile', error);
      return null;
    }
    return data as GoProfile | null;
  }, []);

  /** In-flight profile load, shared so concurrent auth events reuse one call. */
  const profileLoadRef = useRef<{ authUserId: string; promise: Promise<void> } | null>(null);

  /**
   * Load the users row + organisation for an auth user. Accounts are
   * provisioned by invitation (main app / admin portal), so a signed-in user
   * with no users row simply has no profile — the app shows a clear message.
   *
   * init() and the INITIAL_SESSION / SIGNED_IN auth events both request the
   * profile on first load, so concurrent calls for the same user share one
   * promise instead of racing each other.
   */
  const loadProfile = useCallback(
    (authUserId: string): Promise<void> => {
      const inflight = profileLoadRef.current;
      if (inflight && inflight.authUserId === authUserId) return inflight.promise;

      const promise = (async () => {
        const profileRow = await fetchProfileRow(authUserId);

        setProfile(profileRow);
        if (profileRow?.organisation_id) {
          setOrganisation(await fetchOrganisation(profileRow.organisation_id));
        } else {
          setOrganisation(null);
        }
      })();

      profileLoadRef.current = { authUserId, promise };
      void promise.finally(() => {
        if (profileLoadRef.current?.promise === promise) {
          profileLoadRef.current = null;
        }
      });
      return promise;
    },
    [fetchProfileRow, fetchOrganisation],
  );

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      const {
        data: { session: currentSession },
      } = await supabase.auth.getSession();
      if (cancelled) return;

      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      if (currentSession?.user) {
        await loadProfile(currentSession.user.id);
      }
      if (!cancelled) setLoading(false);
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setUser(newSession?.user ?? null);
      if (newSession?.user) {
        loadProfile(newSession.user.id);
      } else {
        setProfile(null);
        setOrganisation(null);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [loadProfile]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      if (data.user) {
        await loadProfile(data.user.id);
      }
    },
    [loadProfile],
  );

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
    setOrganisation(null);
  }, []);

  const refreshOrganisation = useCallback(async () => {
    if (profile?.organisation_id) {
      setOrganisation(await fetchOrganisation(profile.organisation_id));
    }
  }, [profile?.organisation_id, fetchOrganisation]);

  // The person-level campaigns gate. Only the campaigns host asks; on Go the
  // question is meaningless and the query would be a wasted round trip.
  useEffect(() => {
    if (!profile?.id || appSurface() === 'go') {
      setHasCampaignsAuthority(undefined);
      return;
    }
    let cancelled = false;
    supabase
      .from('user_authorities')
      .select('id, authority_roles!inner(role_code)')
      .eq('user_id', profile.id)
      .eq('organisation_id', profile.organisation_id)
      .eq('is_active', true)
      .eq('authority_roles.role_code', CAMPAIGNS_AUTHORITY_CODE)
      .limit(1)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          logger.warn('Could not resolve campaigns authority', error);
          // Fail closed: an unreadable authority is not permission.
          setHasCampaignsAuthority(false);
          return;
        }
        setHasCampaignsAuthority((data ?? []).length > 0);
      });
    return () => {
      cancelled = true;
    };
  }, [profile?.id, profile?.organisation_id]);

  const value: AuthContextType = {
    user,
    session,
    profile,
    organisation,
    loading,
    hasCampaignsAuthority,
    isOwner: profile?.role === 'sme_admin',
    signIn,
    signOut,
    refreshOrganisation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
